import React from 'react';
import { Client, ClientTranscript } from '../../types/client';
import { getClientInitials, formatDate, getSentimentBadge, copyToClipboard } from '../../utils/clientUtils';

interface ClientTranscriptsListProps {
  selectedClient: Client | null;
  transcripts: ClientTranscript[];
  selectedTranscript: ClientTranscript | null;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  totalTranscripts: number;
  onTranscriptSelect: (transcript: ClientTranscript) => void;
  onEditClient: (client: Client) => void;
  onDeleteClient: (clientId: string) => void;
  onLoadMore: () => void;
}

const ClientTranscriptsList: React.FC<ClientTranscriptsListProps> = ({
  selectedClient,
  transcripts,
  selectedTranscript,
  loading,
  loadingMore,
  hasMore,
  totalTranscripts,
  onTranscriptSelect,
  onEditClient,
  onDeleteClient,
  onLoadMore
}) => {
  if (!selectedClient) {
    return (
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="flex items-center justify-center h-full p-6 text-center">
          <div>
            <div className="text-gray-400 text-4xl mb-4">👆</div>
            <p className="text-gray-600">Select a client to view their transcripts</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if client has any transcripts (disable delete if they do)
  const hasTranscripts = totalTranscripts > 0;

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
      {/* Client Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-lg font-medium text-blue-700">
            {getClientInitials(selectedClient.first_name, selectedClient.last_name)}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">
              {selectedClient.first_name} {selectedClient.last_name}
            </h3>
            <p className="text-sm text-gray-500">{selectedClient.email}</p>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => onEditClient(selectedClient)}
              className="p-2 text-gray-400 hover:text-blue-600 rounded"
              title="Edit Client"
            >
              ✏️
            </button>
            {!hasTranscripts && (
              <button
                onClick={() => onDeleteClient(selectedClient.id)}
                className="p-2 text-gray-400 hover:text-red-600 rounded"
                title="Delete Client"
              >
                🗑️
              </button>
            )}
            {hasTranscripts && (
              <button
                disabled
                className="p-2 text-gray-300 rounded cursor-not-allowed"
                title="Cannot delete client with associated transcripts"
              >
                🗑️
              </button>
            )}
          </div>
        </div>
        
        <div className="text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span>📞 {selectedClient.phone_primary}</span>
            <button
              onClick={() => copyToClipboard(selectedClient.phone_primary, 'Phone number')}
              className="p-1 text-gray-400 hover:text-blue-600 rounded text-xs"
              title="Copy phone number"
            >
              📋
            </button>
          </div>
          {selectedClient.email && (
            <div className="flex items-center gap-2 mt-1">
              <span>✉️ {selectedClient.email}</span>
              <button
                onClick={() => copyToClipboard(selectedClient.email || '', 'Email')}
                className="p-1 text-gray-400 hover:text-blue-600 rounded text-xs"
                title="Copy email address"
              >
                📋
              </button>
            </div>
          )}
          {selectedClient.company_name && <p className="mt-1">🏢 {selectedClient.company_name}</p>}
        </div>
      </div>

      {/* Transcripts List - Restructured to match working TranscriptList pattern */}
      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p className="text-gray-600 text-sm">Loading transcripts...</p>
            </div>
          ) : transcripts.length === 0 ? (
            <div className="p-6 text-center">
              <div className="text-gray-400 text-3xl mb-3">📄</div>
              <p className="text-gray-600 text-sm">No transcripts found</p>
              <p className="text-gray-500 text-xs mt-1">
                This client can be safely deleted
              </p>
            </div>
          ) : (
            <div className="space-y-1 p-2">
              {transcripts.map((transcript) => (
                <div
                  key={transcript.transcript_id}
                  onClick={() => onTranscriptSelect(transcript)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedTranscript?.transcript_id === transcript.transcript_id
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                      {transcript.title}
                    </h4>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>📅 {formatDate(transcript.created_at)}</span>
                      <span>•</span>
                      <span>📝 {transcript.word_count} words</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {transcript.sentiment_label && (
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getSentimentBadge(
                            transcript.sentiment_label
                          )}`}
                        >
                          {transcript.sentiment_label}
                        </span>
                      )}
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                        {transcript.relationship_type}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Load More Button - Outside scrollable area */}
        {hasMore && (
          <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              className="w-full px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  Loading...
                </div>
              ) : (
                `Load More (${totalTranscripts - transcripts.length} remaining)`
              )}
            </button>
          </div>
        )}
        
        {/* Pagination Info - Outside scrollable area */}
        {transcripts.length > 0 && (
          <div className="p-2 text-xs text-gray-500 text-center bg-gray-50 border-t border-gray-200 flex-shrink-0">
            Showing {transcripts.length} of {totalTranscripts} transcripts
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientTranscriptsList; 